// src/components/TaskDropdown.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tasksService } from '../services/tasksService';
import { ROLES } from '../data/users';

const TaskDropdown = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [tasksDate, setTasksDate] = useState(null);
  const dropdownRef = useRef(null);

  // DEBUG: Log para verificar que el componente se renderiza
  console.log('TaskDropdown render - isOpen:', isOpen);

  // Cerrar dropdown si se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Cargar tareas cuando se abra el dropdown
  useEffect(() => {
    if (isOpen && user) {
      console.log('Loading tasks for user:', user.role); // DEBUG
      loadTasks();
      
      // FORZAR POSICIONAMIENTO CON JAVASCRIPT - MÁS AGRESIVO
      if (dropdownRef.current) {
        const dropdown = dropdownRef.current;
        dropdown.style.position = 'fixed';
        dropdown.style.top = '80px';
        dropdown.style.right = '20px';
        dropdown.style.left = 'auto';
        dropdown.style.width = '480px';
        dropdown.style.zIndex = '9999';
        dropdown.style.margin = '0';
        dropdown.style.padding = '0';
        dropdown.style.border = '1px solid #e5e5e5';
        
        // Calcular altura específica hasta el borde - MÁS AGRESIVO
        const windowHeight = window.innerHeight;
        const topPosition = 80;
        const calculatedHeight = windowHeight - topPosition - 10; // Restar 10px para el margen que quieres
        dropdown.style.height = `${calculatedHeight}px`;
        dropdown.style.bottom = 'auto';
        dropdown.style.maxHeight = 'none';
        
        console.log(`Window height: ${windowHeight}, Calculated height: ${calculatedHeight}`); // DEBUG
      }
    }
  }, [isOpen, user]);

  const loadTasks = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await tasksService.getTasksForUser(user.role);
      
      if (result.success) {
        // ✅ ORDENAR TAREAS POR PRIORIDAD: Alta -> Media -> Baja
        const sortedTasks = result.tasks.sort((a, b) => {
          const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        });

        setTasks(sortedTasks);
        setStats(result.byPriority);
        setTasksDate(result.date || null);
      } else {
        setError(result.error);
        setTasks([]);
      }
    } catch (err) {
      console.error('Error cargando tareas:', err);
      setError('Error al cargar las tareas');
      setTasks([]);
    }
    
    setLoading(false);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  const formatTasksDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    const dayName = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][d.getDay()];
    const monthName = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][month - 1];
    return `${dayName} ${day} ${monthName}`;
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case ROLES.ADMIN: return 'Administrador';
      case ROLES.ATENCION_CLIENTE: return 'Atención al Cliente';
      case ROLES.COORDINADOR_OPERACIONES: return 'Coordinador de Operaciones';
      default: return role;
    }
  };

  const handleTaskClick = (task) => {
    console.log('Tarea seleccionada:', task);
    // TODO: Implementar lógica para mostrar detalles de la tarea
    // Podría abrir un modal con los detalles completos
  };

  const markAsCompleted = async (taskId, event) => {
    event.stopPropagation(); // Evitar que se ejecute handleTaskClick
    
    try {
      await tasksService.markTaskAsCompleted(taskId);
      // Recargar tareas
      loadTasks();
    } catch (err) {
      console.error('Error al completar tarea:', err);
    }
  };

  console.log('About to render TaskDropdown, isOpen:', isOpen); // DEBUG

  return (
    <div ref={dropdownRef} className={`task-dropdown ${isOpen ? 'open' : ''}`}>
      {/* Header fijo */}
      <div className="task-dropdown-header">
        <div className="flex justify-between items-center p-4">
          <div>
            <h3 className="font-semibold text-gray-800">
              Tareas pendientes
              {tasksDate && <span className="font-normal text-gray-500"> · {formatTasksDate(tasksDate)}</span>}
            </h3>
            <p className="text-sm text-gray-500">
              {getRoleDisplayName(user?.role)}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Content area que ocupa el resto del espacio */}
      <div className="task-dropdown-content">
        
        {/* Estadísticas fijas */}
        {!loading && !error && stats && (
          <div className="task-stats">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Total: <strong>{tasks.length}</strong></span>
              <div className="flex gap-4">
                <span className="text-red-600 font-medium">🔴 {stats.high}</span>
                <span className="text-yellow-600 font-medium">🟡 {stats.medium}</span>
                <span className="text-green-600 font-medium">🟢 {stats.low}</span>
              </div>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="loading-container">
            <div className="text-gray-500 flex items-center gap-2">
              <div className="loading-spinner"></div>
              Cargando tareas...
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="error-container">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 max-w-sm">
              <p className="font-medium">Error</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Sin tareas */}
        {!loading && !error && tasks.length === 0 && (
          <div className="no-tasks">
            <p>🎉 ¡No tienes tareas pendientes!</p>
            <small>Todas las órdenes de ayer están en orden</small>
          </div>
        )}

        {/* Lista de tareas con scroll */}
        {!loading && !error && tasks.length > 0 && (
          <div className="task-list-container">
            {tasks.map((task) => (
              <div 
                key={task.id} 
                className={`task-item cursor-pointer priority-${task.priority}`}
                onClick={() => handleTaskClick(task)}
              >
                <div className="task-content">
                  {/* Header de la tarea */}
                  <div className="task-header">
                    <div className="task-priority-indicator">
                      <span className="priority-icon">{getPriorityIcon(task.priority)}</span>
                      <span className={`priority-badge ${getPriorityColor(task.priority)}`}>
                        {task.priority === 'high' ? 'Alta' : 
                         task.priority === 'medium' ? 'Media' : 'Baja'}
                      </span>
                    </div>
                    <button
                      onClick={(e) => markAsCompleted(task.id, e)}
                      className="complete-button"
                      title="Marcar como completada"
                    >
                      ✓
                    </button>
                  </div>

                  {/* Información principal */}
                  <div className="task-main-info">
                    <div className="task-reference">
                      {task.workorderData.fullReference || `WO #${task.workorderId}`}
                    </div>
                    <div className="task-state-name">
                      Estado: {task.workorderData.state}
                    </div>
                  </div>

                  {/* Descripción de la tarea */}
                  <div className="task-description">
                    📝 {task.description}
                  </div>

                  {/* Info adicional */}
                  <div className="task-additional-info">
                    {task.workorderData.customerName && (
                      <span className="task-info-item">
                        👤 {task.workorderData.customerName.length > 30 
                          ? `${task.workorderData.customerName.substring(0, 30)}...`
                          : task.workorderData.customerName}
                      </span>
                    )}
                    {task.workorderData.assigneeName && (
                      <span className="task-info-item">
                        🔧 {task.workorderData.assigneeName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Footer fijo con botón actualizar */}
      <div className="task-footer">
        <button 
          onClick={loadTasks}
          className="refresh-button"
          disabled={loading}
        >
          🔄 Actualizar tareas
        </button>
      </div>
    </div>
  );
};

export default TaskDropdown;